import type { Card } from "@braze/web-sdk";
import { env } from "config/env";
import { atom, getDefaultStore } from "jotai";

export const contentCardsAtom = atom<Array<Card>>([]);
const closedContentCardIdsAtom = atom(new Set<string | undefined>());
export const contentCardsOpenAtom = atom((get) => {
  const closedIds = get(closedContentCardIdsAtom);
  return get(contentCardsAtom).some((card) => !closedIds.has(card.id));
});

const cardIds = (cards: Array<Card>) => cards.map((card) => card.id).join(",");

const getBraze = async () => {
  if (env.IS_LOCAL || !env.REACT_APP_BRAZE_KEY || !env.REACT_APP_BRAZE_ENDPOINT) return null;
  const braze = await import("@braze/web-sdk");
  if (braze.isInitialized()) return braze;
  braze.initialize(env.REACT_APP_BRAZE_KEY, {
    baseUrl: env.REACT_APP_BRAZE_ENDPOINT,
    serviceWorkerLocation: "/braze-service-worker.js",
  });
  return braze;
};

const initBraze = async (userId: string) => {
  const braze = await getBraze();
  if (!braze) return;
  braze.requestPushPermission();
  braze.automaticallyShowInAppMessages();
  if (braze.getUser()?.getUserId() !== userId) {
    getDefaultStore().set(contentCardsAtom, []);
    getDefaultStore().set(closedContentCardIdsAtom, new Set());
  }
  braze.changeUser(userId);
  braze.openSession();
  braze.subscribeToContentCardsEvents((event) => {
    if (!("cacheSnapshot" in event)) return;
    const { cards } = event.cacheSnapshot.contentCards;
    const store = getDefaultStore();
    if (cardIds(store.get(contentCardsAtom)) !== cardIds(cards)) store.set(contentCardsAtom, cards);
  });
  braze.requestContentCardsRefresh();
};

const closeContentCards = async () => {
  const braze = await getBraze();
  if (!braze) return;
  const store = getDefaultStore();
  const cards = store.get(contentCardsAtom);
  for (const card of cards) {
    if ("dismissible" in card && card.dismissible === true) braze.logCardDismissal(card);
  }
  store.set(closedContentCardIdsAtom, new Set(cards.map((card) => card.id)));
};

export { initBraze, getBraze, closeContentCards };
